import { X, Minus, Plus, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";
import { useCart, formatUSD } from "@/context/CartContext";

export default function CartDrawer() {
    const {
        items,
        subtotal,
        donation,
        total,
        donate,
        setDonate,
        updateQty,
        removeItem,
        open,
        setOpen,
    } = useCart();

    return (
        <>
            {open && (
                <div
                    className="fixed inset-0 z-50 bg-forest/60 backdrop-blur-sm"
                    onClick={() => setOpen(false)}
                    data-testid="cart-overlay"
                />
            )}
            <aside
                className={`fixed top-0 right-0 h-full w-full sm:w-[440px] bg-cream z-50 shadow-2xl transform transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)] ${
                    open ? "translate-x-0" : "translate-x-full"
                }`}
                data-testid="cart-drawer"
            >
                <div className="flex flex-col h-full">
                    <header className="px-6 pt-8 pb-5 flex items-center justify-between border-b border-forest/10">
                        <div>
                            <p className="text-[0.65rem] tracking-[0.35em] uppercase text-brown">
                                Your Basket
                            </p>
                            <h3 className="font-serif text-2xl text-forest mt-1">
                                The Glow Set
                            </h3>
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            className="text-forest hover:text-gold transition-colors"
                            aria-label="Close cart"
                            data-testid="cart-close-button"
                        >
                            <X size={22} />
                        </button>
                    </header>

                    <div className="flex-1 overflow-y-auto px-6 py-4">
                        {items.length === 0 ? (
                            <div
                                className="text-center py-20"
                                data-testid="cart-empty-state"
                            >
                                <div className="divider-rule text-brown mb-6">
                                    empty
                                </div>
                                <p className="font-serif text-2xl text-forest">
                                    The shelf is bare.
                                </p>
                                <p className="text-sm text-brown/70 mt-2">
                                    Add a glow to begin.
                                </p>
                                <Link
                                    to="/shop"
                                    onClick={() => setOpen(false)}
                                    className="btn-pill btn-primary mt-8"
                                    data-testid="cart-go-shop-btn"
                                >
                                    Explore the Collection
                                </Link>
                            </div>
                        ) : (
                            <ul className="divide-y divide-forest/10">
                                {items.map((item) => (
                                    <li
                                        key={item.slug}
                                        className="py-5 flex gap-4"
                                        data-testid={`cart-line-${item.slug}`}
                                    >
                                        <div className="w-20 h-24 bg-cream-warm overflow-hidden rounded-sm shrink-0">
                                            <img
                                                src={item.image}
                                                alt={item.name}
                                                className="w-full h-full object-cover"
                                            />
                                        </div>
                                        <div className="flex-1 flex flex-col">
                                            <div className="flex justify-between items-start gap-2">
                                                <div>
                                                    <p className="font-serif text-lg text-forest leading-tight">
                                                        {item.name}
                                                    </p>
                                                    <p className="text-xs text-brown mt-1">
                                                        {formatUSD(item.price)}{" "}
                                                        · 4.5 oz
                                                    </p>
                                                </div>
                                                <button
                                                    onClick={() =>
                                                        removeItem(item.slug)
                                                    }
                                                    className="text-brown/60 hover:text-forest transition-colors"
                                                    aria-label="Remove"
                                                    data-testid={`cart-remove-${item.slug}`}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                            <div className="mt-auto flex items-center justify-between">
                                                <div className="inline-flex items-center border border-forest/20 rounded-full">
                                                    <button
                                                        onClick={() =>
                                                            updateQty(
                                                                item.slug,
                                                                item.quantity -
                                                                    1,
                                                            )
                                                        }
                                                        className="w-8 h-8 flex items-center justify-center text-forest hover:text-gold"
                                                        aria-label="Decrease"
                                                        data-testid={`cart-qty-dec-${item.slug}`}
                                                    >
                                                        <Minus size={14} />
                                                    </button>
                                                    <span className="w-8 text-center font-serif text-forest text-base">
                                                        {item.quantity}
                                                    </span>
                                                    <button
                                                        onClick={() =>
                                                            updateQty(
                                                                item.slug,
                                                                item.quantity +
                                                                    1,
                                                            )
                                                        }
                                                        className="w-8 h-8 flex items-center justify-center text-forest hover:text-gold"
                                                        aria-label="Increase"
                                                        data-testid={`cart-qty-inc-${item.slug}`}
                                                    >
                                                        <Plus size={14} />
                                                    </button>
                                                </div>
                                                <span className="font-serif text-forest">
                                                    {formatUSD(
                                                        item.price *
                                                            item.quantity,
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {items.length > 0 && (
                        <div
                            className="border-t border-forest/10 px-6 py-5 bg-cream-warm/40"
                            data-testid="cart-summary"
                        >
                            {/* Philanthropy toggle */}
                            <label
                                className="flex items-start gap-3 p-4 rounded-sm border border-gold/40 bg-cream mb-4 cursor-pointer hover:border-gold transition-colors"
                                data-testid="cart-donate-toggle-label"
                            >
                                <input
                                    type="checkbox"
                                    checked={donate}
                                    onChange={(e) => setDonate(e.target.checked)}
                                    className="mt-1 h-4 w-4 accent-[color:var(--ig-gold)]"
                                    data-testid="cart-donate-toggle"
                                />
                                <span className="text-sm text-forest leading-relaxed">
                                    <span className="font-semibold tracking-wide uppercase text-[0.7rem] text-gold mr-1">
                                        Round-Up
                                    </span>
                                    Round up 10% to fund the{" "}
                                    <span className="font-serif italic">
                                        CyberKids Academy
                                    </span>{" "}
                                    — code-literacy for island youth.
                                </span>
                            </label>

                            <div className="space-y-1.5 text-sm text-forest">
                                <div className="flex justify-between">
                                    <span className="text-brown/80">
                                        Subtotal
                                    </span>
                                    <span
                                        className="font-medium"
                                        data-testid="cart-subtotal"
                                    >
                                        {formatUSD(subtotal)}
                                    </span>
                                </div>
                                {donate && (
                                    <div
                                        className="flex justify-between text-gold"
                                        data-testid="cart-donation-line"
                                    >
                                        <span>CyberKids Round-Up (10%)</span>
                                        <span>{formatUSD(donation)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between pt-2 mt-2 border-t border-forest/10 font-serif text-lg">
                                    <span>Total</span>
                                    <span data-testid="cart-total">
                                        {formatUSD(total)}
                                    </span>
                                </div>
                            </div>

                            <Link
                                to="/cart"
                                onClick={() => setOpen(false)}
                                className="btn-pill btn-primary w-full justify-center mt-5"
                                data-testid="cart-checkout-btn"
                            >
                                Proceed to Checkout
                            </Link>
                            <p className="text-center text-[0.65rem] tracking-[0.3em] uppercase text-brown/60 mt-3">
                                Secure · Zero-Cap Checkout
                            </p>
                        </div>
                    )}
                </div>
            </aside>
        </>
    );
}
